import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  User, Users, Sparkles, CreditCard, Plug, ScrollText, ClipboardList, Mail, Building2, Settings2, GitBranch,
  Globe2, Briefcase, CalendarCheck, LayoutGrid, Code2, Languages, MessageSquare, Gauge, LucideIcon, ChevronRight,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Select } from '@/components/Select';

export interface SettingsTab { to: string; label: string; icon: LucideIcon; description: string; end?: boolean }
export interface SettingsSectionDef { title: string; icon: LucideIcon; blurb: string; tabs: SettingsTab[] }

export const SETTINGS_SECTIONS: SettingsSectionDef[] = [
  { title: 'Account', icon: User, blurb: 'You, your teammates and what they did.', tabs: [
    { to: '/settings/profile', label: 'My profile', icon: User, description: 'Name, phone, password and two-step verification.' },
    { to: '/settings/team', label: 'Team', icon: Users, description: 'Invite teammates, set roles and lead routing.' },
    { to: '/settings/departments', label: 'Departments', icon: Briefcase, description: 'Sales, support, delivery — and who belongs where.' },
    { to: '/settings/activity', label: 'Activity log', icon: ScrollText, description: 'Every change made in this workspace.' },
  ]},
  { title: 'Workspace', icon: Building2, blurb: 'How the CRM is set up for your business.', tabs: [
    { to: '/settings/business-profile', label: 'Business profile', icon: Building2, description: 'What you sell, your tone and languages — the AI reads this.' },
    { to: '/settings/industry', label: 'Industry & pages', icon: LayoutGrid, description: 'Your industry setup and which pages the team sees.' },
    { to: '/settings/localization', label: 'Localisation', icon: Globe2, description: 'Country, currency, timezone and VAT defaults.' },
    { to: '/settings/custom-fields', label: 'Custom fields', icon: Settings2, description: 'Extra fields on contacts, leads and deals.' },
    { to: '/settings/pipelines', label: 'Pipelines', icon: GitBranch, description: 'Deal stages for each sales process.' },
  ]},
  { title: 'Channels', icon: Plug, blurb: 'Where customers reach you and where data comes in.', tabs: [
    { to: '/settings/integrations', label: 'Integrations', icon: Plug, description: 'Your WhatsApp number and Meta (Facebook & Instagram) connection.' },
    { to: '/settings/email-inboxes', label: 'Email inboxes', icon: Mail, description: 'Turn incoming email into leads.' },
    { to: '/settings/lead-ad-forms', label: 'Lead ad forms', icon: ClipboardList, description: 'Meta instant forms and their auto-replies.' },
    { to: '/settings/booking-site', label: 'Booking site', icon: CalendarCheck, description: 'Public booking link, kiosk and reminders.' },
    { to: '/settings/developer', label: 'Developer API', icon: Code2, description: 'API keys and webhooks for your own systems.' },
  ]},
  { title: 'AI', icon: Sparkles, blurb: 'Which models answer, in which language, and what it may cost.', tabs: [
    { to: '/settings/ai', label: 'Providers & models', icon: Sparkles, description: 'Managed AI or your own keys; default model.', end: true },
    { to: '/settings/ai/languages', label: 'Language defaults', icon: Languages, description: 'A different model per customer language.' },
    { to: '/settings/ai/whatsapp', label: 'WhatsApp fallback', icon: MessageSquare, description: 'Let the AI reply when no flow matches.' },
    { to: '/settings/ai/usage', label: 'Cost caps & usage', icon: Gauge, description: 'Daily / monthly limits and credits used.' },
  ]},
  { title: 'Billing', icon: CreditCard, blurb: 'Your plan, limits and renewal.', tabs: [
    { to: '/settings/plan', label: 'Plan & usage', icon: CreditCard, description: 'Current plan, usage this month and module seats.' },
  ]},
];

const ALL_TABS = SETTINGS_SECTIONS.flatMap((s) => s.tabs.map((t) => ({ ...t, section: s.title })));

export function activeSettingsTab(pathname: string) {
  const clean = pathname.replace(/\/+$/, '');
  return ALL_TABS.find((t) => clean === t.to) ?? ALL_TABS.find((t) => !t.end && clean.startsWith(`${t.to}/`));
}

export const SettingsShell: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const current = activeSettingsTab(pathname);
  const isHub = pathname.replace(/\/+$/, '') === '/settings';

  return (
    <>
      <Topbar
        title="Settings"
        crumbs={isHub || !current ? [{ label: 'Settings', ...(isHub ? {} : { to: '/settings' }) }] : [{ label: 'Settings', to: '/settings' }, { label: current.section }]}
        autoBack={false}
      />
      {isHub ? (
        <div className="w-full px-4 py-6 sm:px-7 sm:py-7"><Outlet /></div>
      ) : (
        <div className="grid w-full gap-6 px-4 py-6 sm:px-7 sm:py-7 lg:grid-cols-[220px_minmax(0,1fr)] xl:grid-cols-[248px_minmax(0,1fr)]">
          <aside className="min-w-0">
            {/* Below lg the app sidebar already takes the width — phones and small tablets get a picker. */}
            <label className="block max-w-sm lg:hidden">
              <span className="sr-only">Settings page</span>
              <Select
                value={current?.to ?? ''}
                onChange={(e) => navigate(e.target.value)}
                className="h-11 w-full rounded-xl border border-border bg-surface px-3 text-sm font-medium text-text-primary focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary-soft"
              >
                {SETTINGS_SECTIONS.map((s) => (
                  <optgroup key={s.title} label={s.title}>
                    {s.tabs.map((t) => <option key={t.to} value={t.to}>{t.label}</option>)}
                  </optgroup>
                ))}
              </Select>
            </label>

            <nav className="hidden lg:sticky lg:top-24 lg:block" aria-label="Settings">
              <NavLink to="/settings" end className="mb-4 flex items-center gap-1.5 px-3 text-xs font-medium text-text-muted transition-colors hover:text-primary">
                <ChevronRight size={13} className="rotate-180" /> All settings
              </NavLink>
              <div className="space-y-5">
                {SETTINGS_SECTIONS.map((s) => (
                  <div key={s.title}>
                    <div className="mb-1 px-3 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">{s.title}</div>
                    <div className="space-y-0.5">
                      {s.tabs.map((t) => (
                        <NavLink
                          key={t.to}
                          to={t.to}
                          end={t.end}
                          className={({ isActive }) => clsx(
                            'relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-colors',
                            isActive
                              ? 'bg-primary-soft font-semibold text-primary after:absolute after:bottom-2 after:left-0 after:top-2 after:w-0.5 after:rounded-full after:bg-primary'
                              : 'text-text-secondary hover:bg-surface-alt hover:text-text-primary',
                          )}
                        >
                          <t.icon size={15} className="shrink-0" /> <span className="truncate">{t.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </nav>
          </aside>
          <div className="min-w-0">
            <Outlet />
          </div>
        </div>
      )}
    </>
  );
};
